// PHASE 0 (Route A) — deterministic "you can also…" ACTION SUGGESTIONS off a platform_qa answer.
//
// After Otto ANSWERS a how/where/what question, it can also offer to ACT on it. The answer already cites the
// knowledge-base entries it used, and those keys are TYPED — `action.<capability>.<act>`, `page.<slug>`,
// `concept.<slug>` (see platform_qa/inventory.py). This module turns those cited, typed entries into a short,
// GROUNDED list of suggestion chips, WITHOUT a new LLM call:
//   - concept.<dim>  -> "Filter by <dim>"  (only when the CURRENT page can filter)
//   - action.<cap>.<act> -> a concrete directive ("sort the table")  (only when <cap> is mounted HERE)
//   - page.<slug>    -> "Open <page>"       (only when reachable + not already a deep-link nav button)
//
// GROUNDING is the whole point (same rule as nav deep-links): never suggest an action Otto can't actually
// perform in this context. A concept isn't offered where filtering isn't mounted; an action isn't offered
// where its capability isn't mounted; a page isn't offered if it isn't reachable. Clicking a chip re-enters
// the NORMAL pipeline (a directive is re-submitted → the capability runs its own grounding + confirm; a
// navigate drives the real router) — nothing is auto-executed here. Deterministic → frontend-tested, no eval.
import { pageActionLabel } from "../planner/pageManifest";

// Domain CONCEPTS that map to a real, grounded filter dimension. The FALLBACK allowlist for callers with no
// live maps: a chip must lead to something Otto can actually filter by (clicking re-submits "filter by X",
// which the filter flow grounds). Toggles / purely-conceptual entries (cost_basis, dashboard_source,
// recommendation_vs_capital_request) are intentionally excluded — "filter by" them is not a real filter.
// With `maps` provided (FTO-11), the LIVE field set decides instead — the same anti-stale-list rule as the
// resolver's baseline probe — and the chip says what it now actually does: a browsable dimension opens the
// FTO-10 browse card ("Browse <label> options"), a numeric/date wildcard the FTO-12 threshold ask
// ("Set a <label> threshold"). Directives use the META's exact label, which is precisely what the
// field-mention gate detects — the chip can never drift from the card it promises.
const FILTERABLE_CONCEPTS = new Set(["criticality", "trade", "system", "category", "priority_band"]);

// The maps-grounded concept chip, or null when the concept names no live field (fallback list applies).
const conceptChipFromMaps = (slug, maps, isBrowseable) => {
    if (!maps || !maps.dimMap) return null;
    const meta = maps.dimMap[slug];
    if (meta && meta.label && !meta.isBoolean && meta.value_kind !== "text" && typeof isBrowseable === "function" && isBrowseable(meta)) {
        return { kind: "directive", label: `Browse ${meta.label} options`, directive: `filter by ${meta.label.toLowerCase()}` };
    }
    const wc = maps.wildcardMap ? maps.wildcardMap[slug] : null;
    if (wc && wc.label && (wc.value_type === "number" || wc.value_type === "date")) {
        return { kind: "directive", label: `Set a ${wc.label} threshold`, directive: `filter by ${wc.label.toLowerCase()}` };
    }
    return null;
};

// Cited ACTIONS that make a sensible STANDALONE directive on their capability. `<capability>.<action>` →
// the natural phrase re-submitted on click (the capability then grounds any target it needs, e.g. which
// column to sort). Vague/target-hungry actions (hide_column, pin, page-size, column_filter) are omitted —
// they don't stand alone as a useful one-tap suggestion.
const ACTION_DIRECTIVES = {
    "charts.chart_type": "change the chart type",
    "charts.export": "export the chart",
    "table_view.sort": "sort the table",
    "table_view.export": "export the table",
    "dashboard_reset.reset": "reset the dashboard"
};

const MAX_SUGGESTIONS = 3;
const capitalize = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// citations: [{ key, label, target? }] — the typed KB keys, each already carrying its grounded destination
// when it names a page Otto can open (qaTargets.js resolves it once, in qaProposal, so the catalog lookup
// and the KB-route fallback live in ONE place). page: current page id. pages: the capability manifest
// (appContext.pages = [{ page, capabilities }]). deepLinks: already-built nav buttons on the card.
// Returns up to MAX_SUGGESTIONS: [{ kind: "directive"|"navigate", label, directive?, target? }].
export const buildActionSuggestions = ({ citations, page, pages, deepLinks, maps, isBrowseable } = {}) => {
    const cites = Array.isArray(citations) ? citations : [];
    if (!cites.length) return [];

    const currentCaps = new Set(((pages || []).find(p => p && p.page === page) || {}).capabilities || []);
    // Filtering is available here if the page has the dashboard `filters` capability OR a data-grid
    // (`table_view` → its column_filter). No filter surface → no concept-filter chips (grounding).
    const canFilterHere = currentCaps.has("filters") || currentCaps.has("table_view");
    const linkedTargetKeys = new Set((deepLinks || []).map(d => d && d.target && d.target.key).filter(Boolean));

    const directives = []; // the NEW value (act on the answer) — prioritized over navigation
    const navs = [];

    for (const c of cites) {
        const key = (c && c.key) || "";
        const parts = key.split(".");
        const type = parts[0];
        if (type === "concept" && canFilterHere) {
            // Live maps decide first (any browsable/threshold field earns its chip); the hand-kept
            // allowlist stays as the fallback for concepts outside the field set and for map-less callers.
            const grounded = conceptChipFromMaps(parts[1], maps, isBrowseable);
            if (grounded) {
                directives.push(grounded);
            } else if (FILTERABLE_CONCEPTS.has(parts[1])) {
                const label = (c.label || "").trim();
                if (label) directives.push({ kind: "directive", label: `Filter by ${label}`, directive: `filter by ${label.toLowerCase()}` });
            }
        } else if (type === "action") {
            const cap = parts[1];
            const directive = ACTION_DIRECTIVES[`${cap}.${parts[2]}`];
            if (directive && currentCaps.has(cap)) directives.push({ kind: "directive", label: capitalize(directive), directive });
        } else if (type === "page") {
            // The citation's OWN grounded target (nav catalog first, else the entry's KB route) — resolved
            // upstream so the "Open …" chip and the tappable source chip can never disagree about where a
            // cited page is. A page already offered as a "Take me there" button is skipped (one CTA per
            // destination); the quiet source chip is not a CTA, so it doesn't suppress this one.
            const target = c && c.target;
            // "Open <page>" — a data-grid page reads "Open Recommendations Table View" (pageActionLabel).
            if (target && !linkedTargetKeys.has(target.key)) navs.push({ kind: "navigate", label: pageActionLabel("Open", target), target });
        }
    }

    // Directives first (the point of the feature), then navigation to fill remaining slots. Dedupe by the
    // action taken (directive text / nav target), so repeated citations can't produce duplicate chips.
    const out = [];
    const seen = new Set();
    for (const s of [...directives, ...navs]) {
        const dedupeKey = `${s.kind}|${s.directive || (s.target && s.target.key) || s.label}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        out.push(s);
        if (out.length >= MAX_SUGGESTIONS) break;
    }
    return out;
};
